import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { parseFile, renderTemplate } from './crud';
import { useStore } from '@/store';
import { useRenderTemplate } from '../../hooks/use-render-template';
import { findAllOccurrences } from '@morph-mapper/utils';
import { getFileKey } from '@/utils';
import { NodeId } from '../../components/graph-editor/types';
import {
  NodeEvalRequest,
  NodeEvalResult,
} from '../../components/graph-editor/lib/batch/types';
import {
  findEntryResults,
  withPositionSelection,
} from './parse-utils';
import { useParseHighlight } from './use-parse-highlight';
import { mapGraphEvaluationResponse } from './graph-evaluation-response';

/**
 * Hook selector for parsing partially rendered template code with
 * the text content of the current loaded example file.
 *
 * NOTE: Options currently not supported
 */
export const useEntryParser = (entryId: string, nodeId?: string) => {
  const [variant, textContent] = useStore((s) => [
    s.config.getVariant(),
    s.config.getTextContent(),
    s.entries,
    s.graphs,
  ]);
  const { renderEntryWithContext } = useRenderTemplate();
  const template = renderEntryWithContext(entryId, nodeId);

  const queryResult = useQuery({
    queryKey: ['entry-parser', entryId, nodeId, variant, template, textContent],
    queryFn: template
      ? ({ signal }) =>
          renderTemplate(variant, template, textContent, {}, signal)
      : skipToken,
    select: withPositionSelection((renderedData) =>
      findAllOccurrences(renderedData, `__reserved__key__${entryId}`),
    ),
  });

  return useParseHighlight(queryResult.data);
};

// TODO: move and improve typing, possibly testing what we get back to support type inferrence for the graph.
type ReservedKey<Id extends string = string> = `__reserved__key__${Id}`;
export type GraphNodeParserResult = {
  data: Record<
    NodeId,
    {
      fingerprint: number;
      position: unknown;
      [k: ReservedKey]: unknown;
    }
  >;
};

type InternalNodeEvalRequest = Record<NodeId, unknown>;
// TODO: We should do away with custom unwrapping and the notiong of reserved keys.
export const useGraphParser = () => {
  const [variant, textContent] = useStore(({ config: c, graphs: g }) => [
    c.getVariant(),
    c.getTextContent(),
  ]);

  const maptoInternal = (reqs: NodeEvalRequest[]) => {
    return reqs.reduce((acc, req) => {
      acc[req.nodeId] = req.template;
      return acc;
    }, {} as InternalNodeEvalRequest);
  };

    return useMutation({
    mutationFn: async ({
      entryId,
      reqs,
    }: {
      entryId: string;
      reqs: NodeEvalRequest[];
    }): Promise<NodeEvalResult[]> => {
      const res = await renderTemplate(
        variant,
        maptoInternal(reqs),
        textContent,
        {},
      );

      return mapGraphEvaluationResponse(entryId, reqs, res);
    },
  });
};

export const renderDirtyEntries = (
  variant: Parameters<typeof renderTemplate>[0],
  template: Record<string, any>,
  textContent: string,
  signal: AbortSignal,
) => {
  const validTemplate = Object.fromEntries(
    Object.entries(template).filter(([, value]) => value !== undefined),
  );

  return renderTemplate(variant, validTemplate, textContent, {}, signal);
};

export const useDirtyEntry = (entryIds: string[]) => {
  const [variant, textContent] = useStore(({ config: c }) => [
    c.getVariant(),
    c.getTextContent(),
  ]);

  const { renderEntryWithContext } = useRenderTemplate();

  const template = entryIds.reduce(
    (acc, entryId) => {
      acc[entryId] = renderEntryWithContext(entryId);
      return acc;
    },
    {} as Record<string, any>,
  );

  const parseData = (data: unknown) => {
    return entryIds.reduce(
      (acc, entryId) => {
        const values = findEntryResults(data, entryId);
        return {
          ...acc,
          // TODO: Typed results so we can distinguish between different targets.
          // Probably requires rerouting through the current express API.
          [entryId]: values,
        };
      },
      {} as Record<string, any>,
    );
  };

  const shouldQuery = entryIds.length > 0;

  const queryResult = useQuery({
    queryKey: ['dirty-entry', entryIds, variant, template, textContent],
    queryFn: shouldQuery
      ? ({ signal }) =>
          renderDirtyEntries(variant, template, textContent, signal)
      : skipToken,
    select: withPositionSelection((renderedData) => parseData(renderedData)),
  });

  return {
    ...useParseHighlight(queryResult.data),
    error: queryResult.error,
  };
};

/**
 * Hook selector for parsing the complete rendered template code with
 * the text content of the current loaded example file.
 *
 * NOTE: Options currently not supported
 */
export const useTemplateParser = (template: any) => {
  const [variant, textContent] = useStore(({ config: c }) => [
    c.getVariant(),
    c.getTextContent(),
  ]);

  return useQuery({
    queryKey: ['template-parser', variant, template, textContent],
    queryFn: template
      ? ({ signal }) =>
          renderTemplate(variant, template, textContent, {}, signal)
      : skipToken,
  });
};

/**
 * Hook selector for parsing the text content of the current loaded example file.
 */
export const useFileToTextParser = (
  file: File | undefined,
  parseTrigger = 0,
) => {
  const variant = useStore(({ config: c }) => c.getVariant());
  const fileKey = getFileKey(file);

  return useQuery({
    queryKey: ['file-to-text', variant, fileKey, parseTrigger],
    queryFn: file ? () => parseFile(variant, file) : skipToken,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });
};
